import {
  NotificationResponseDto,
  NotificationResponseDtoEventEnum,
  UserResponseDtoRoleEnum,
} from '@app/api';
import { COLORS } from '@app/constants';
import { ROUTES } from '@app/routers';
import { selectors } from '@app/store';
import { useAppSelector } from '@app/store/hooks';
import { NotificationFriendlyData } from '@app/types';
import { getFormatedDate } from '@app/utils/getFormatedDate';
import Link from 'next/link';
import styled from 'styled-components';

const defaultNotificationData = {
  title: '',
  description: '',
  date: getFormatedDate(new Date(), 'Pp'),
};

interface NotificationData extends Omit<NotificationResponseDto, 'metadata'> {
  metadata: Record<string, any>;
}

export const useNotificationDescription = (
  data: NotificationData,
): NotificationFriendlyData => {
  const userRole = useAppSelector(selectors.auth.selectRole);

  switch (data.event) {
    case NotificationResponseDtoEventEnum.ConsultationCreated:
      return getConsultationCreateData(data, userRole);
    case NotificationResponseDtoEventEnum.ConsultationCanceled:
      return getConsultationCanceledData(data, userRole);
    case NotificationResponseDtoEventEnum.ConsultationAwaiting:
      return getConsultationAwaitingData(data, userRole);
    case NotificationResponseDtoEventEnum.ConsultationShift:
      return getConsultationShiftData(data, userRole);
    case NotificationResponseDtoEventEnum.ConsultationStart:
      return getConsultationStartData(data, userRole);
    case NotificationResponseDtoEventEnum.ConsultationNote:
      return getConsultationNoteData(data);
    case NotificationResponseDtoEventEnum.Profile:
      return getProfileData(data);
    case NotificationResponseDtoEventEnum.Payment:
      return getPaymentData(data);
    case NotificationResponseDtoEventEnum.Payout:
      return getPayoutData(data);
    case NotificationResponseDtoEventEnum.Approve:
      return getApproveData(data);
    case NotificationResponseDtoEventEnum.NewPromocodes:
      return getNewPromocodesData(data);
    case NotificationResponseDtoEventEnum.PromocodesRunningOut:
      return getPromocodesRunningOut(data);
    case NotificationResponseDtoEventEnum.PromocodesExpiring:
      return getPromocodesExpiringData(data);
    case NotificationResponseDtoEventEnum.TherapistVerify:
      return getTherapistVerifyData(data);
    case NotificationResponseDtoEventEnum.PaymentMethod:
      return getPaymentMethodData(data);
    case NotificationResponseDtoEventEnum.GreetingsPatient:
      return getPatientGreetingsData(data);
    case NotificationResponseDtoEventEnum.GreetingsTherapist:
      return getTherapistGreetingsData(data);
    case NotificationResponseDtoEventEnum.PaymentByTherapistRejected:
      return getPaymentByTherapistRejectedData(data);

    default:
      return defaultNotificationData;
  }
};

const getPaymentByTherapistRejectedData = (
  { popUpDate, metadata }: NotificationData,
): NotificationFriendlyData => {
  const notificationDate = getFormatedDate(popUpDate, 'Pp');
    return {
      title: (
        <p style={{ color: 'red' }}>Оплата за консультацию была отклонена</p>
      ),
      description: `Оплата за консультацию у ${metadata.therapist.name} была отклонена`,
      date: notificationDate,
    };
};

const getConsultationCreateData = (
  { popUpDate, metadata }: NotificationData,
  role: UserResponseDtoRoleEnum,
): NotificationFriendlyData => {
  const notificationDate = getFormatedDate(popUpDate, 'Pp');
  const consultationDate = getFormatedDate(metadata.startDate, 'PPP');
  const consultationTime = getFormatedDate(metadata.startDate, 'p');

  if (role === UserResponseDtoRoleEnum.Patient) {
    return {
      title: `Вы записаны на консультацию к ${metadata.consultant.name}`,
      description: `Консультация состоится ${consultationDate} в ${consultationTime}`,
      date: notificationDate,
    };
  }
  if (role === UserResponseDtoRoleEnum.Therapist) {
    return {
      title: `У вас новая консультация с ${metadata.clients[0].name}`,
      description: `Консультация состоится ${consultationDate} в ${consultationTime}`,
      date: notificationDate,
    };
  }

  return defaultNotificationData;
};

const getConsultationShiftData = (
  { popUpDate, metadata }: NotificationData,
  role: UserResponseDtoRoleEnum,
): NotificationFriendlyData => {
  const notificationDate = getFormatedDate(popUpDate, 'Pp');
  const consultationDate = getFormatedDate(metadata.startDate, 'PPP');
  const consultationTime = getFormatedDate(metadata.startDate, 'p');

  if (role === UserResponseDtoRoleEnum.Patient) {
    return {
      title: `Консультация с ${metadata.consultant.name} перенесена`,
      description: `Теперь она состоится ${consultationDate} в ${consultationTime}`,
      date: notificationDate,
    };
  }

  if (role === UserResponseDtoRoleEnum.Therapist) {
    return {
      title: `${metadata.clients[0].name} перенес(-ла) консультацию`,
      description: `Теперь она состоится ${consultationDate} в ${consultationTime}`,
      date: notificationDate,
    };
  }

  return defaultNotificationData;
};

const getConsultationCanceledData = (
  { popUpDate, metadata }: NotificationData,
  role: UserResponseDtoRoleEnum,
): NotificationFriendlyData => {
  const notificationDate = getFormatedDate(popUpDate, 'Pp');
  const consultationDate = getFormatedDate(metadata.startDate, 'PPP');
  const consultationTime = getFormatedDate(metadata.startDate, 'p');

  if (role === UserResponseDtoRoleEnum.Patient) {
    return {
      title: `${metadata.cancelerName} отменил сессию ${consultationDate} в ${consultationTime}`,
      description: 'Правила отмены бронирования вы найдете в FAQ',
      date: notificationDate,
    };
  }

  if (role === UserResponseDtoRoleEnum.Therapist) {
    return {
      title: `${metadata.cancelerName} отменил сессию`,
      description: `Причина: ${metadata.cancelReason}`,
      date: notificationDate,
    };
  }

  return defaultNotificationData;
};

const getConsultationAwaitingData = (
  { popUpDate, metadata }: NotificationData,
  role: UserResponseDtoRoleEnum,
): NotificationFriendlyData => {
  const notificationDate = getFormatedDate(popUpDate, 'Pp');
  const consultationDate = getFormatedDate(metadata.startDate, 'PPP');
  const consultationTime = getFormatedDate(metadata.startDate, 'p');

  if (role === UserResponseDtoRoleEnum.Patient) {
    return {
      title: `У вас состоится консультация с ${metadata.consultant.name} ${consultationDate} в ${consultationTime}`,
      description: 'Не пропустите',
      date: notificationDate,
    };
  }

  if (role === UserResponseDtoRoleEnum.Therapist) {
    return {
      title: `У вас состоится консультация с ${metadata.clients[0].name} ${consultationDate} в ${consultationTime}`,
      description: 'Не пропустите',
      date: notificationDate,
    };
  }

  return defaultNotificationData;
};

const getConsultationStartData = (
  { popUpDate, metadata }: NotificationData,
  role: UserResponseDtoRoleEnum,
): NotificationFriendlyData => {
  const notificationDate = getFormatedDate(popUpDate, 'Pp');

  if (role === UserResponseDtoRoleEnum.Patient) {
    return {
      title: `Консультация с ${metadata.consultant.name} начнется через 5 минут`,
      description: 'Не пропустите',
      date: notificationDate,
    };
  }

  if (role === UserResponseDtoRoleEnum.Therapist) {
    return {
      title: `Консультация с ${metadata.clients[0].name} начнется через 5 минут`,
      description: 'Не пропустите',
      date: notificationDate,
    };
  }

  return defaultNotificationData;
};

const getProfileData = ({
  popUpDate,
  metadata,
}: NotificationData): NotificationFriendlyData => {
  const formatedDate = getFormatedDate(popUpDate, 'Pp');

  if (metadata.accepted) {
    return {
      title: 'Обновления профиля приняты',
      description: 'Проверьте изменения',
      date: formatedDate,
    };
  } else if (!metadata.accepted) {
    return {
      title: 'Обновления профиля отклонены',
      description: 'Уточните причину у администратора',
      date: formatedDate,
    };
  }

  return defaultNotificationData;
};

const getApproveData = ({
  popUpDate,
  metadata,
}: NotificationData): NotificationFriendlyData => {
  const formatedDate = getFormatedDate(popUpDate, 'Pp');

  if (metadata.approved) {
    return {
      title: 'Вас сделали одобренным специалистом',
      description: 'Удачного пользования нашим сервисом',
      date: formatedDate,
    };
  } else if (!metadata.approved) {
    return {
      title: 'С вас сняли отметку одобренного специалиста',
      description: 'Причину уточняйте у администратора',
      date: formatedDate,
    };
  }

  return defaultNotificationData;
};

const getPaymentData = ({
  popUpDate,
  metadata,
}: NotificationData): NotificationFriendlyData => {
  const formatedDate = getFormatedDate(popUpDate, 'Pp');
  const consultationDate = getFormatedDate(
    metadata.consultation.startDate,
    'PPP',
  );
  const consultantName = metadata.consultation.consultant.name;

  if (metadata.status === 'canceled')
    return {
      title: 'Консультация отменена',
      description: `Консультация на ${consultationDate} с ${consultantName} была отменена`,
      date: formatedDate,
    };
  if (metadata.status === 'waiting_for_capture')
    return {
      title: 'Консультация оплачена',
      description: `Консультация на ${consultationDate} с ${consultantName} была оплачена`,
      date: formatedDate,
    };

  return defaultNotificationData;
};

const getPayoutData = ({
  popUpDate,
  metadata,
}: NotificationData): NotificationFriendlyData => {
  const formatedDate = getFormatedDate(popUpDate, 'Pp');
  const consultationDate = getFormatedDate(
    metadata.consultation.startDate,
    'PPP',
  );
  const consultantName = metadata.consultation.consultant.name;

  if (metadata.status === 'canceled')
    return {
      title: 'Консультация отменена',
      description: `Консультация на ${consultationDate} с ${consultantName} была отменена`,
      date: formatedDate,
    };
  if (metadata.status === 'waiting_for_capture')
    return {
      title: 'Консультация оплачена',
      description: `Консультация на ${consultationDate} с ${consultantName} была оплачена`,
      date: formatedDate,
    };

  return defaultNotificationData;
};

const getNewPromocodesData = ({
  popUpDate,
  metadata,
}: NotificationData): NotificationFriendlyData => {
  const formatedDate = getFormatedDate(popUpDate, 'Pp');
  const expirationDate = getFormatedDate(metadata.expirationDate, 'Pp');

  return {
    title: `Добавлено ${metadata.count} новых промокодов`,
    description: `Срок действия до ${expirationDate}`,
    date: formatedDate,
  };
};

const getPromocodesExpiringData = ({
  popUpDate,
  metadata,
}: NotificationData): NotificationFriendlyData => {
  const formatedDate = getFormatedDate(popUpDate, 'Pp');
  const expirationDate = getFormatedDate(metadata.expirationDate, 'Pp');

  return {
    title: `Действия промокодов истекает ${expirationDate}`,
    description: 'Обратитесь к администратору',
    date: formatedDate,
  };
};

const getPromocodesRunningOut = ({
  popUpDate,
}: NotificationData): NotificationFriendlyData => {
  const formatedDate = getFormatedDate(popUpDate, 'Pp');

  return {
    title: 'У вас кончаются промокоды',
    description: 'Обратитесь к администратору',
    date: formatedDate,
  };
};

const getConsultationNoteData = ({
  popUpDate,
  metadata,
}: NotificationData): NotificationFriendlyData => {
  const formatedDate = getFormatedDate(popUpDate, 'Pp');

  return {
    title: `Новое сообщение в чате сессии с ${metadata.user.name}`,
    description: `Сообщение: ${metadata.comment}`,
    date: formatedDate,
  };
};

const getTherapistVerifyData = ({
  popUpDate,
}: NotificationData): NotificationFriendlyData => {
  const formatedDate = getFormatedDate(popUpDate, 'Pp');

  return {
    title: 'Ваш аккаунт прошел проверку',
    description:
      'Мы рассмотрели вашу заявку и рады сообщить, что Ваша кандидатура одобрена!',
    date: formatedDate,
  };
};

const getPaymentMethodData = ({
  popUpDate,
  metadata,
}: NotificationData): NotificationFriendlyData => {
  const formatedDate = getFormatedDate(popUpDate, 'Pp');

  if (metadata.attached) {
    return {
      title: 'К вашему аккаунту привязан метод оплаты',
      description: 'Теперь ваш профиль доступен в поиске!',
      date: formatedDate,
    };
  } else {
    return {
      title: 'К вашему аккаунту не привязан метод оплаты',
      description: 'Без метода оплаты ваш профиль не доступен в поиске!',
      date: formatedDate,
    };
  }
};

const getPatientGreetingsData = ({
  popUpDate,
}: NotificationData): NotificationFriendlyData => {
  const formatedDate = getFormatedDate(popUpDate, 'Pp');

  return {
    title: 'Добро пожаловать в Поговорим.online! ✨',
    description: (
      <div>
        <p style={{ display: 'block', marginBottom: 3, marginTop: 7 }}>
          Мы рады встрече и верим, что наш совместный путь будет долгим и
          прекрасным 👐🏻
        </p>
        <p style={{ display: 'block', marginBottom: 10 }}>
          Мы заботимся о том, чтобы ваше время с нами было запоминающимся и
          полезным, чтобы все сложилось именно так, предлагаем обратить внимание
          на важное:
        </p>
        <div style={{ paddingLeft: '10px', marginBottom: 10 }}>
          <span
            style={{ fontWeight: 'bold', display: 'block', marginBottom: 5 }}
          >
            ⏰ Время
          </span>
          <p style={{ display: 'block', marginBottom: 10 }}>
            Сессия длится 50 мин. Не опаздывайте, чтобы не терять драгоценных
            минут
          </p>
          <span
            style={{ fontWeight: 'bold', display: 'block', marginBottom: 5 }}
          >
            📍Пространство
          </span>
          <p style={{ display: 'block', marginBottom: 10 }}>
            Это время только для вас и про вас, поэтому важно быть одному, не
            находиться в пути или движении, иметь хорошую устойчивую связь,
            попросить не беспокоить вас в течение 50 минут.
          </p>
          <span
            style={{ fontWeight: 'bold', display: 'block', marginBottom: 5 }}
          >
            🪫Проверка оборудования
          </span>
          <p style={{ display: 'block', marginBottom: 10 }}>
            Перед первой сессией обязательно проверьте работу камеры и микрофона
            <Link href={ROUTES['/test-call']} passHref>
              <StyledLink>{' по ссылке'}</StyledLink>
            </Link>
          </p>
          <span
            style={{ fontWeight: 'bold', display: 'block', marginBottom: 5 }}
          >
            ❗️Правило отмены
          </span>
          <p style={{ display: 'block', marginBottom: 10 }}>
            Если вы хотите отменить или перенести сессию, необходимо это сделать
            не позднее, чем за 24 часа до ее начала.
          </p>
          <span
            style={{ fontWeight: 'bold', display: 'block', marginBottom: 5 }}
          >
            ❓Подбор специалиста
          </span>
          <p style={{ display: 'block', marginBottom: 10 }}>
            Для того, чтобы найти своего специалиста, воспользуйтесь фильтрами
            на сайте или пройдите наш опросник.
            <br />
            Также всегда можно обратиться в клиентскую поддержку, наша команда
            заботливо подберет самых подходящих психологов персонально под ваш
            запрос.
          </p>
          <span
            style={{ fontWeight: 'bold', display: 'block', marginBottom: 5 }}
          >
            💌 Мы на связи
          </span>
          <p style={{ display: 'block', marginBottom: 10 }}>
            Вы всегда можете написать нам в чат поддержки внизу страницы либо в
            <StyledLink
              href="https://wa.me/+79198707755"
              rel="noopener noreferrer"
            >
              {' WhatsApp '}
            </StyledLink>
            🦥🌿
          </p>
        </div>
        <div>Мы рады вам!</div>
        <div>В добрый путь! ❤️</div>
      </div>
    ),
    date: formatedDate,
  };
};

const getTherapistGreetingsData = ({
  popUpDate,
}: NotificationData): NotificationFriendlyData => {
  const formatedDate = getFormatedDate(popUpDate, 'Pp');

  return {
    title: 'Добро пожаловать в команду Поговорим.online!',
    description: (
      <div>
        <p style={{ display: 'block', marginBottom: 3, marginTop: 7 }}>
          Мы рады встрече и верим, что наш совместный путь будет долгим и
          прекрасным 👐🏻
        </p>
        <p style={{ display: 'block', marginBottom: 10 }}>
          Мы очень надеемся, что наш союз будет долгим и прекрасным 😊
          <br />
          Мы заботимся о качестве вашего времени с нами и предлагаем обратить
          внимание на то, что важно, чтобы все сложилось:
        </p>
        <div style={{ paddingLeft: '10px', marginBottom: 10 }}>
          <span
            style={{ fontWeight: 'bold', display: 'block', marginBottom: 5 }}
          >
            💸 Платежи
          </span>
          <p style={{ display: 'block', marginBottom: 10 }}>
            Не забывайте привязывать карты в профиле в разделе «платежи», без
            этого ваш профиль не будет публично отображаться
          </p>
          <span
            style={{ fontWeight: 'bold', display: 'block', marginBottom: 5 }}
          >
            🪫Проверка оборудования
          </span>
          <p style={{ display: 'block', marginBottom: 10 }}>
            Перед первой сессией обязательно проверьте работу камеры и микрофона
            <Link href={ROUTES['/test-call']} passHref>
              <StyledLink>{' по ссылке'}</StyledLink>
            </Link>
          </p>
          <span
            style={{ fontWeight: 'bold', display: 'block', marginBottom: 5 }}
          >
            ❗️Оповещения
          </span>
          <p style={{ display: 'block', marginBottom: 10 }}>
            О записи клиента мы будем уведомлять вас по смс. Но! Лучше лишний
            раз зайти на платформу и свериться, нет ли у вас новых записей,
            чтобы точно не пропустить клиента.
          </p>
          <span
            style={{ fontWeight: 'bold', display: 'block', marginBottom: 5 }}
          >
            📅 Расписание
          </span>
          <p style={{ display: 'block', marginBottom: 10 }}>
            Не забывайте регулярно проставлять окна на неделю вперед. Профили,
            чье расписание не обновлялось в течение месяца будут скрыты с
            платформы. Вы можете удалить незанятые рабочие слоты день в день,
            если захотите. Помните, что клиент может занять слот не позднее, чем
            за 2 часа до начала сессии.
          </p>
          <span
            style={{ fontWeight: 'bold', display: 'block', marginBottom: 5 }}
          >
            💌 Стоимость консультации
          </span>
          <p style={{ display: 'block', marginBottom: 10 }}>
            Пожалуйста, убедитесь что стоимость вашей сессии на платформе
            коррелирует со стоимостью в других открытых источниках. Мы очень за,
            чтобы каждый терапевт сам определял свою стоимость НО! стоимость у
            нас не должна отличаться от стоимости на вашем личном сайте или
            других агрегаторах больше, чем на 500 рублей.
          </p>
          <span
            style={{ fontWeight: 'bold', display: 'block', marginBottom: 5 }}
          >
            🙇🏼‍♀️ Анкета
          </span>
          <p style={{ display: 'block', marginBottom: 10 }}>
            Фото профиля должно быть высокого разрешения, поза и выражения лица
            открытыми и доброжелательными. Желательно, чтобы в кадре вас было
            видно по пояс, это доказано вызывает больше доверия у клиента на
            подсознательном уровне. И конечно записывайте видео, клиенты очень
            активно смотрят видеовизитки!
          </p>
          <span
            style={{ fontWeight: 'bold', display: 'block', marginBottom: 5 }}
          >
            📈 Активность и продвижение
          </span>
          <p style={{ display: 'block', marginBottom: 10 }}>
            Участвуйте в написании текстов и статей для наших соц. сетей и
            партнерских спецпроектов, клиент узнает ваше лицо и имя, придя на
            платформу, это хорошая дополнительная реклама для вас.
          </p>
        </div>
        <div>В добрый путь!</div>
        <div>❤️</div>
      </div>
    ),
    date: formatedDate,
  };
};

const StyledLink = styled.a`
  color: ${COLORS.blue400};
`;
